import type { AnyAction, Middleware, MiddlewareAPI } from "@reduxjs/toolkit";
import { isFulfilled, isPending, isRejected } from "@reduxjs/toolkit";
import { LoadingState, setError, setIsLoading } from "./slice/appStateSlice";

export interface IAppLoaderAction {
  componentId?: string;
}

interface IStrapiErrorMessage {
  id: string;
  message: string;
}

interface IStrapiErrorEnvelope {
  error: { message: string };
}

interface IStrapiValidationErrorEnvelope {
  data: Array<{ messages: Array<IStrapiErrorMessage> }>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function hasComponentId(value: unknown): value is IAppLoaderAction & { componentId: string } {
  return isRecord(value) && typeof value.componentId === "string";
}

function isReduxAction(value: unknown): value is AnyAction {
  return isRecord(value) && typeof value.type !== "undefined";
}

function isUnknownArray(value: unknown): value is Array<unknown> {
  return Array.isArray(value);
}

function isStrapiErrorEnvelope(value: unknown): value is IStrapiErrorEnvelope {
  return isRecord(value) && isRecord(value.error) && typeof value.error.message === "string";
}

function isStrapiValidationErrorEnvelope(value: unknown): value is IStrapiValidationErrorEnvelope {
  if (!isRecord(value) || !isUnknownArray(value.data) || value.data.length === 0) {
    return false;
  }
  const [firstEntry] = value.data;
  return isRecord(firstEntry) && isUnknownArray(firstEntry.messages);
}

export const loadingHandler: Middleware = (api: MiddlewareAPI) => (next) => (action: unknown) => {
  if (!isReduxAction(action)) {
    return next(action as AnyAction);
  }

  if (isPending(action) && hasComponentId(action.meta.arg)) {
    const { componentId } = action.meta.arg;
    api.dispatch(setError({ componentId, error: "" }));
    api.dispatch(setIsLoading({ componentId, loading: LoadingState.Loading }));
  }
  if (isFulfilled(action) && hasComponentId(action.meta.arg)) {
    const { componentId } = action.meta.arg;
    api.dispatch(setIsLoading({ componentId, loading: LoadingState.Success }));
  }
  if (isRejected(action) && hasComponentId(action.meta.arg)) {
    const { componentId } = action.meta.arg;
    api.dispatch(setIsLoading({ componentId, loading: LoadingState.Idle }));
    try {
      if (isStrapiErrorEnvelope(action.payload)) {
        api.dispatch(setError({ componentId, error: action.payload.error.message }));
      } else if (action.error.message) {
        api.dispatch(setError({ componentId, error: action.error.message }));
      } else if (isStrapiValidationErrorEnvelope(action.payload)) {
        const errorMessages = action.payload.data[0].messages
          .map((errorElem) => errorElem.message)
          .join(" ");
        api.dispatch(setError({ componentId, error: errorMessages }));
      } else {
        api.dispatch(setError({ componentId, error: "An error occured" }));
      }
    } catch (ex) {
      console.error(ex);
      api.dispatch(setError({ componentId, error: "An error occured" }));
    }
  }

  return next(action);
};
